/**
 * `diagram/serialize.ts` — `DiagramModel` → mermaid source (interactive-diagrams-v0.1.md §5.1).
 *
 * The serializer's contract is **minimal-diff** (plan §5.1, §8): an unedited statement
 * re-emits its exact original bytes; only edited or newly-created elements are generated
 * canonically. This is what makes `serialize(parse(src)) === src` hold, and — the reason it
 * matters — what makes an agent regenerating a diagram (or a user dragging one node) touch
 * only the lines that actually changed, keeping revision history and human/agent merges sane.
 *
 * The model's `statements` array is the single source of truth for serialization. Every edit
 * op here returns a *new* model with a rewritten statements array (existing statements kept by
 * reference, so their `raw` is preserved verbatim); `serialize` just joins the `raw` fields.
 */

import {
  type DiagramModel,
  type DiagramNode,
  type DiagramEdge,
  type Statement,
  type NodeShape,
  type ConnectorStyle,
  type EdgeArrow,
  type SidecarData,
  SHAPE_DELIMS,
  edgeKey,
} from './model';
import { serializeSidecar, isSidecarEmpty, mergeSidecar } from './sidecar';
import { indentOf } from './parse';

/**
 * Serialize a model to mermaid source. Pure `raw` passthrough over the statement spine, so
 * an unedited model round-trips byte-for-byte. Edits are already baked into the statements by
 * the edit ops below, so this stays trivial and total.
 */
export function serialize(model: DiagramModel): string {
  return model.statements.map((s) => s.raw).join('\n');
}

// ─────────────────────────────────────────────────────────────────────────────
// Canonical emitters (used for edited / newly-created elements only)
// ─────────────────────────────────────────────────────────────────────────────

/** Shape → its emit delimiters. First `SHAPE_DELIMS` entry per shape name (each is unique). */
const SHAPE_EMIT: Record<NodeShape, { open: string; close: string }> = (() => {
  const m = {} as Record<NodeShape, { open: string; close: string }>;
  m.none = { open: '', close: '' };
  for (const { shape, open, close } of SHAPE_DELIMS) {
    if (!(shape in m)) m[shape] = { open, close };
  }
  return m;
})();

/** Canonical source for a node token: `id`, `id[Label]`, `id["quoted"]`, plus `:::class` refs. */
export function nodeToSource(node: DiagramNode): string {
  let s = node.id;
  if (node.shape !== 'none') {
    const { open, close } = SHAPE_EMIT[node.shape];
    const label = node.quoted ? `"${node.label}"` : node.label;
    s += `${open}${label}${close}`;
  }
  for (const c of node.classes) s += `:::${c}`;
  return s;
}

/** Canonical connector string for a (style, arrow) pair, e.g. solid+arrow → `-->`. */
export function connectorToSource(connector: ConnectorStyle, arrow: EdgeArrow): string {
  if (connector === 'dotted') {
    return arrow === 'open' ? '-.-' : '-.->';
  }
  if (connector === 'thick') {
    return arrow === 'open' ? '===' : '==>';
  }
  // solid
  switch (arrow) {
    case 'open':
      return '---';
    case 'circle':
      return '--o';
    case 'cross':
      return '--x';
    case 'bi':
      return '<-->';
    default:
      return '-->';
  }
}

/**
 * Canonical source for an edge line. `fromRef`/`toRef` are node source strings (bare ids for
 * edges between already-declared nodes, or full `id[Label]` to declare inline). A label is
 * emitted in canonical pipe form (`-->|label|`).
 */
export function edgeToSource(
  fromRef: string,
  toRef: string,
  connector: ConnectorStyle,
  arrow: EdgeArrow,
  label?: string,
): string {
  const conn = connectorToSource(connector, arrow);
  const connWithLabel = label ? `${conn}|${label}|` : conn;
  return `${fromRef} ${connWithLabel} ${toRef}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Model rebuild helper
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Rebuild a `DiagramModel` from a new statements array, recomputing the merged views and
 * `source`. Keeps the round-trip invariant: `model.source` is set to the serialized text so a
 * re-`parse` of the output is stable. Re-uses the parser's finalize logic indirectly by
 * recomputing nodes/edges/subgraphs/sidecar from statements.
 */
function rebuild(base: DiagramModel, statements: Statement[]): DiagramModel {
  const nodes = new Map<string, DiagramNode>();
  const edges: DiagramEdge[] = [];
  const styles = new Map<string, Record<string, string>>();
  const pairCounts = new Map<string, number>();
  let sidecar: SidecarData | null = null;

  const registerNode = (n: DiagramNode) => {
    const existing = nodes.get(n.id);
    if (!existing || (existing.shape === 'none' && n.shape !== 'none')) nodes.set(n.id, n);
  };

  for (const st of statements) {
    if (st.kind === 'node') registerNode(st.node);
    else if (st.kind === 'edge') {
      for (const n of st.nodes) registerNode(n);
      for (const e of st.edges) {
        const pk = `${e.from}->${e.to}`;
        const ordinal = pairCounts.get(pk) ?? 0;
        pairCounts.set(pk, ordinal + 1);
        e.ordinal = ordinal;
        e.key = edgeKey(e.from, e.to, ordinal);
        edges.push(e);
      }
    } else if (st.kind === 'sidecar') {
      // Deep-merge (last-wins per leaf) so multiple sidecar lines fold correctly.
      sidecar = mergeSidecar(sidecar, st.data);
    } else if (st.kind === 'style') {
      styles.set(st.id, { ...(styles.get(st.id) ?? {}), ...st.props });
    }
  }

  const source = statements.map((s) => s.raw).join('\n');
  return { ...base, statements, nodes, edges, styles, sidecar, source };
}

// ─────────────────────────────────────────────────────────────────────────────
// Sidecar application (the geometry-change path — the core drag use case)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Apply a new sidecar payload to the model, rewriting only the sidecar line(s) (plan §5.1
 * "a drag touches only the sidecar line"). Behavior:
 *   - existing sidecar line(s): the first is replaced with the canonical serialization; any
 *     extras are dropped (collapse-to-one).
 *   - no existing sidecar + non-empty payload: a canonical line is appended after the last
 *     non-blank statement (before any trailing blank lines).
 *   - empty payload: all sidecar lines are removed (a fully-reset diagram carries no sidecar).
 *
 * Indentation is preserved from the first existing sidecar line (so a sidecar inside a
 * subgraph stays indented), else top-level.
 */
export function applySidecar(model: DiagramModel, next: SidecarData | null): DiagramModel {
  const sidecarIdxs = model.statements
    .map((s, i) => (s.kind === 'sidecar' ? i : -1))
    .filter((i) => i >= 0);

  const indent = sidecarIdxs.length > 0 ? indentOf(model.statements[sidecarIdxs[0]].raw) : '';
  const line = next ? serializeSidecar(next, indent) : '';
  const empty = !next || isSidecarEmpty(next) || line === '';

  // Annotated as Statement[]: TS 5.5 would otherwise infer a type-predicate here and narrow
  // away the 'sidecar' variant, blocking re-insertion of a new sidecar statement below.
  let statements: Statement[] = model.statements.filter((s) => s.kind !== 'sidecar');

  if (!empty) {
    const newStmt: Statement = { kind: 'sidecar', raw: line, data: next };
    if (sidecarIdxs.length > 0) {
      // Re-insert at the original first sidecar's slot. Only sidecar statements were removed,
      // and none precede the first one, so its index is unshifted in the filtered array.
      const insertAt = sidecarIdxs[0];
      statements = [...statements.slice(0, insertAt), newStmt, ...statements.slice(insertAt)];
    } else {
      statements = insertAfterLastContent(statements, newStmt);
    }
  }

  return rebuild(model, statements);
}

/** Insert `stmt` after the last non-blank statement (keeping trailing blank lines at the end). */
function insertAfterLastContent(statements: Statement[], stmt: Statement): Statement[] {
  let lastContent = -1;
  for (let i = 0; i < statements.length; i += 1) {
    if (statements[i].raw.trim() !== '') lastContent = i;
  }
  const at = lastContent + 1;
  return [...statements.slice(0, at), stmt, ...statements.slice(at)];
}

// ─────────────────────────────────────────────────────────────────────────────
// Structural edit ops (append-minimal-diff; the canvas CRUD lands on these in Phase 2)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The indentation used by the diagram's body statements, so appended/rewritten lines match the
 * surrounding style (mermaid conventionally indents fence bodies two spaces). Falls back to two
 * spaces when there is no existing node/edge line to sample.
 */
function bodyIndent(model: DiagramModel): string {
  for (const st of model.statements) {
    if (st.kind === 'edge' || st.kind === 'node') return indentOf(st.raw);
  }
  return '  ';
}

/**
 * Append a new node definition as its own statement. Emits a canonical node line after the
 * last content statement. No-op-returns the same model if the id already exists (a node is
 * declared once; re-declaring is the caller's error to avoid).
 */
export function addNode(model: DiagramModel, node: DiagramNode): DiagramModel {
  if (model.nodes.has(node.id)) return model;
  const stmt: Statement = { kind: 'node', raw: bodyIndent(model) + nodeToSource(node), node };
  return rebuild(model, insertAfterLastContent(model.statements, stmt));
}

/**
 * Append a new edge as its own statement, referencing both endpoints by bare id. Any endpoint
 * not yet declared is created as a bare-id node implicitly (mermaid semantics — an edge to an
 * undeclared id introduces it). Emits one canonical edge line; nothing else changes.
 */
export function addEdge(
  model: DiagramModel,
  from: string,
  to: string,
  opts: { connector?: ConnectorStyle; arrow?: EdgeArrow; label?: string } = {},
): DiagramModel {
  const connector = opts.connector ?? 'solid';
  const arrow = opts.arrow ?? 'arrow';
  const raw = bodyIndent(model) + edgeToSource(from, to, connector, arrow, opts.label);
  const mkBare = (id: string): DiagramNode => ({ id, label: id, shape: 'none', quoted: false, classes: [] });
  const edge: DiagramEdge = { from, to, ordinal: 0, connector, arrow, label: opts.label, key: '' };
  const stmt: Statement = { kind: 'edge', raw, nodes: [mkBare(from), mkBare(to)], edges: [edge] };
  return rebuild(model, insertAfterLastContent(model.statements, stmt));
}

/**
 * Replace a node's label (and optionally shape), rewriting *only* that node's definition
 * statement in place. Finds the statement that structurally defines the node (a `node`
 * statement, or the inline definition on an `edge` statement) and regenerates its canonical
 * source. If the node is only ever referenced by bare id, a standalone definition statement
 * is appended instead.
 */
export function updateNodeLabel(
  model: DiagramModel,
  id: string,
  label: string,
  shape?: NodeShape,
): DiagramModel {
  const statements = [...model.statements];
  let touched = false;

  for (let i = 0; i < statements.length; i += 1) {
    const st = statements[i];
    if (st.kind === 'node' && st.node.id === id && st.node.shape !== 'none') {
      const node: DiagramNode = { ...st.node, label, shape: shape ?? st.node.shape, quoted: needsQuote(label) };
      // Preserve the original line's indentation so the rewrite is a true minimal diff.
      statements[i] = { kind: 'node', raw: indentOf(st.raw) + nodeToSource(node), node };
      touched = true;
      break;
    }
  }

  if (!touched) {
    // No standalone def — try an inline definition on an edge chain (`A[Start] --> B`).
    for (let i = 0; i < statements.length; i += 1) {
      const st = statements[i];
      if (st.kind !== 'edge') continue;
      const def = st.nodes.find((n) => n.id === id && n.shape !== 'none');
      if (!def) continue;
      const updated: DiagramNode = {
        ...def,
        label,
        shape: shape ?? def.shape,
        quoted: needsQuote(label),
      };
      // Regenerate just this chain statement with the relabeled node substituted (canonicalizes
      // this one line; siblings untouched). Bare references to the id elsewhere stay bare.
      const newNodes = st.nodes.map((n) => (n.id === id && n.shape !== 'none' ? updated : n));
      const chain = edgeChainStatement(newNodes, st.edges);
      statements[i] = { ...chain, raw: indentOf(st.raw) + chain.raw };
      touched = true;
      break;
    }
  }

  if (!touched) {
    // Node is only ever a bare reference — append a standalone def so the label is expressed.
    const node: DiagramNode = {
      id,
      label,
      shape: shape ?? 'rectangle',
      quoted: needsQuote(label),
      classes: [],
    };
    return rebuild(model, insertAfterLastContent(statements, { kind: 'node', raw: nodeToSource(node), node }));
  }

  return rebuild(model, statements);
}

/** True if a label must be double-quoted to survive mermaid parsing (contains a delimiter char). */
function needsQuote(label: string): boolean {
  return /[[\](){}<>|"]/.test(label);
}

// ─────────────────────────────────────────────────────────────────────────────
// Structural edits (delete / reconnect) — chain-aware, minimal-diff (Phase 2b)
// ─────────────────────────────────────────────────────────────────────────────

/** The edge-statement variant of `Statement`. */
type EdgeStatement = Extract<Statement, { kind: 'edge' }>;

/** A standalone node statement with canonical source (indented to match the diagram body). */
function nodeStatement(node: DiagramNode, indent = ''): Statement {
  return { kind: 'node', raw: indent + nodeToSource(node), node };
}

/** A canonical edge-chain statement over the given nodes + connecting edges. */
function edgeChainStatement(nodes: DiagramNode[], edges: DiagramEdge[], indent = ''): EdgeStatement {
  let raw = indent + nodeToSource(nodes[0]);
  for (let i = 0; i < edges.length; i += 1) {
    const conn = connectorToSource(edges[i].connector, edges[i].arrow);
    const connLbl = edges[i].label ? `${conn}|${edges[i].label}|` : conn;
    raw += ` ${connLbl} ${nodeToSource(nodes[i + 1])}`;
  }
  return { kind: 'edge', raw, nodes, edges };
}

/**
 * Rewrite one edge-chain statement, keeping only edges for which `keepEdge` is true. A dropped
 * edge splits the chain into runs; each surviving run with edges re-emits as a chain statement,
 * each edge-less run re-emits its node(s) as standalone node statements. `dropId`, when set,
 * removes that node id from edge-less runs entirely (used by `removeNode`).
 */
function rewriteChain(
  stmt: EdgeStatement,
  keepEdge: (e: DiagramEdge) => boolean,
  dropId?: string,
): Statement[] {
  const { nodes, edges } = stmt;
  const indent = indentOf(stmt.raw); // preserve the original chain's indentation
  const runs: Array<{ nodes: DiagramNode[]; edges: DiagramEdge[] }> = [];
  let cur = { nodes: [nodes[0]], edges: [] as DiagramEdge[] };
  for (let i = 0; i < edges.length; i += 1) {
    if (keepEdge(edges[i])) {
      cur.nodes.push(nodes[i + 1]);
      cur.edges.push(edges[i]);
    } else {
      runs.push(cur);
      cur = { nodes: [nodes[i + 1]], edges: [] };
    }
  }
  runs.push(cur);

  const out: Statement[] = [];
  for (const run of runs) {
    if (run.edges.length === 0) {
      const ns = dropId ? run.nodes.filter((n) => n.id !== dropId) : run.nodes;
      for (const n of ns) out.push(nodeStatement(n, indent));
    } else {
      out.push(edgeChainStatement(run.nodes, run.edges, indent));
    }
  }
  return out;
}

/**
 * Remove a single edge (by key), splitting its chain statement around the gap. Everything else
 * is left byte-identical. No-op if the key isn't found.
 */
export function removeEdge(model: DiagramModel, key: string): DiagramModel {
  let changed = false;
  const statements: Statement[] = [];
  for (const st of model.statements) {
    if (!changed && st.kind === 'edge' && st.edges.some((e) => e.key === key)) {
      statements.push(...rewriteChain(st, (e) => e.key !== key));
      changed = true;
    } else {
      statements.push(st);
    }
  }
  return changed ? rebuild(model, statements) : model;
}

/**
 * Remove a node and every edge touching it (mermaid semantics: deleting a node deletes its
 * edges). Drops the node's standalone definition(s) and rewrites any chain statement that
 * references it, leaving surviving sub-chains and isolated nodes intact.
 */
export function removeNode(model: DiagramModel, id: string): DiagramModel {
  const statements: Statement[] = [];
  for (const st of model.statements) {
    if (st.kind === 'node' && st.node.id === id) continue; // drop standalone definition
    if (st.kind === 'edge' && st.nodes.some((n) => n.id === id)) {
      statements.push(...rewriteChain(st, (e) => e.from !== id && e.to !== id, id));
      continue;
    }
    statements.push(st);
  }
  return rebuild(model, statements);
}

/**
 * Reconnect an edge to new endpoints, preserving its connector style + label. Implemented as
 * remove-then-add: the old edge's chain statement is split; the new edge appends one line.
 */
export function reconnectEdge(
  model: DiagramModel,
  oldKey: string,
  newFrom: string,
  newTo: string,
): DiagramModel {
  const old = model.edges.find((e) => e.key === oldKey);
  if (!old) return model;
  const removed = removeEdge(model, oldKey);
  return addEdge(removed, newFrom, newTo, {
    connector: old.connector,
    arrow: old.arrow,
    label: old.label,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Styling + edge-property edits (v0.2 — the Lucid-style toolbar lands on these)
// ─────────────────────────────────────────────────────────────────────────────

/** Serialize a style-prop record to mermaid's `k:v,k:v` form (insertion order). */
function stylePropsToSource(props: Record<string, string>): string {
  return Object.entries(props)
    .map(([k, v]) => `${k}:${v}`)
    .join(',');
}

/**
 * Set / merge / clear a node's **native mermaid style directive** (`style <id> k:v,…`).
 *
 * The plan's §3 rule: color + font styling is *mermaid syntax*, never the sidecar — so a styled
 * diagram renders identically in any external mermaid renderer. Semantics:
 *  - props merge into the node's existing directive (last-wins per property);
 *  - a prop set to `null` is removed;
 *  - an empty result removes the directive line entirely;
 *  - multiple pre-existing directives for the id collapse to one (first position kept).
 * Minimal-diff: only the node's own `style` line is touched (or appended, body-indented).
 */
export function setNodeStyle(
  model: DiagramModel,
  id: string,
  props: Record<string, string | null>,
): DiagramModel {
  // Merge onto the current merged view for this id.
  const merged: Record<string, string> = { ...(model.styles.get(id) ?? {}) };
  for (const [k, v] of Object.entries(props)) {
    if (v === null) delete merged[k];
    else merged[k] = v;
  }

  const styleIdxs = model.statements
    .map((s, i) => (s.kind === 'style' && s.id === id ? i : -1))
    .filter((i) => i >= 0);
  const indent =
    styleIdxs.length > 0 ? indentOf(model.statements[styleIdxs[0]].raw) : bodyIndent(model);
  const empty = Object.keys(merged).length === 0;

  let statements: Statement[] = model.statements.filter(
    (s) => !(s.kind === 'style' && s.id === id),
  );
  if (!empty) {
    const stmt: Statement = {
      kind: 'style',
      raw: `${indent}style ${id} ${stylePropsToSource(merged)}`,
      id,
      props: merged,
    };
    if (styleIdxs.length > 0) {
      // Re-insert at the original first directive's slot (only this id's lines were removed,
      // none precede the first, so the index is unshifted in the filtered array).
      statements = [...statements.slice(0, styleIdxs[0]), stmt, ...statements.slice(styleIdxs[0])];
    } else {
      statements = insertAfterLastContent(statements, stmt);
    }
  }
  return rebuild(model, statements);
}

/**
 * Update an edge's connector style / arrow head / label in place, by key. Rewrites only the
 * chain statement that carries the edge (canonicalizing that one line, indentation preserved);
 * `label: null` clears the label. No-op for an unknown key.
 */
export function updateEdge(
  model: DiagramModel,
  key: string,
  props: { connector?: ConnectorStyle; arrow?: EdgeArrow; label?: string | null },
): DiagramModel {
  const statements: Statement[] = [...model.statements];
  for (let i = 0; i < statements.length; i += 1) {
    const st = statements[i];
    if (st.kind !== 'edge' || !st.edges.some((e) => e.key === key)) continue;
    const newEdges = st.edges.map((e) =>
      e.key === key
        ? {
            ...e,
            connector: props.connector ?? e.connector,
            arrow: props.arrow ?? e.arrow,
            label: props.label === null ? undefined : (props.label ?? e.label),
          }
        : { ...e },
    );
    const chain = edgeChainStatement(st.nodes, newEdges, indentOf(st.raw));
    statements[i] = chain;
    return rebuild(model, statements);
  }
  return model;
}
